import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useState,
  useMemo,
} from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Box, Typography, CircularProgress } from '@material-ui/core';
import {
  FiberManualRecord as DotIcon,
} from '@material-ui/icons';
import { useShellAppContext } from 'features/shell/context/useShellAppContext';
import { enqueueErrorSnackbar } from '../../../../actions/Notifications';
import { getStepsForExperience } from '../config/embeddedWizardConfig';
import StepRouter from '../steps/ConfigStep/StepRouter';
import { useIntegrationConfig } from '../hooks/useIntegrationConfig';
import AnimatedStepSwitcher from '../../../../components/common/AnimatedStepSwitcher';
import IntegrationWizardContext from './IntegrationWizardContext';
import styles from './EmbeddedWizard.module.scss';
import { useLocalizedMessage } from '../../../../util/useLocalizedMessage';

const EmbeddedWizard = forwardRef(({
  experienceType, shopId, client: clientProp, onComplete,
  onReady, onNavigationChange, isExpanded, onExpandToggle,
}, ref) => {
  const localizeMessage = useLocalizedMessage();
  // Read gateway client from shell context — prop takes priority.
  const { gatewayClient: contextClient } = useShellAppContext();
  const client = clientProp || contextClient;

  // All hooks must be declared before any early return (Rules of Hooks).
  const dispatch = useDispatch();
  const [isStepReady, setIsStepReady] = React.useState(true);
  const stepSaveRef = React.useRef(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);

  const {
    config,
    loading: configLoading,
    error: configError,
    saveConfig,
    updateViewIntegration,
  } = useIntegrationConfig(client, shopId);

  const [portalContainer, setPortalContainer] = useState(null);

  useEffect(() => {
    setPortalContainer(document.getElementById('wizard-navigation-center'));
  }, []);

  useEffect(() => {
    if (configError) {
      dispatch(enqueueErrorSnackbar(configError));
    }
  }, [configError, dispatch]);

  const steps = getStepsForExperience(experienceType);
  const currentStep = steps[currentStepIndex];
  const isLastStep = currentStepIndex === steps.length - 1;
  const loading = configLoading;

  useEffect(() => {
    if (onNavigationChange) {
      onNavigationChange({
        canGoNext: isStepReady && !saving && !loading,
        canGoPrev: !saving && !loading,
        isLastStep,
      });
    }
  }, [isStepReady, saving, loading, isLastStep, onNavigationChange]);

  useEffect(() => {
    if (onReady) onReady(!loading);
  }, [onReady, loading]);

  const handleStepReady = useCallback((ready) => {
    setIsStepReady(ready);
  }, []);

  // Callback to allow steps to register a save function
  const handleRegisterSave = useCallback((saveFn) => {
    stepSaveRef.current = saveFn;
  }, []);

  const handleStepUpdate = useCallback((stepData) => {
    setData((d) => ({ ...d, ...stepData }));
  }, []);

  const handleNext = useCallback(async () => {
    if (stepSaveRef.current) {
      setSaving(true);
      try {
        await stepSaveRef.current();
      } catch (e) {
        setSaving(false);
        dispatch(enqueueErrorSnackbar(
          e.message || localizeMessage('wizard.searchSetup.configuration.saveFailed'),
        ));
        return;
      }
      setSaving(false);
    }

    if (isLastStep) {
      if (onComplete) onComplete();
    } else {
      // Clear stale save handler before the new step mounts and registers its own.
      // Using explicit clear here (not a useEffect) ensures ordering: this runs
      // before the new step's useEffect can re-register, so no cross-step leak.
      stepSaveRef.current = null;
      setCurrentStepIndex((i) => Math.min(i + 1, steps.length - 1));
    }
  }, [isLastStep, onComplete, steps.length, dispatch, localizeMessage]);

  const handlePrev = useCallback(() => {
    if (currentStepIndex === 0) return true;
    setCurrentStepIndex((i) => Math.max(i - 1, 0));
    return false;
  }, [currentStepIndex]);

  useImperativeHandle(ref, () => ({
    handleNext,
    handlePrev,
    canGoNext: isStepReady && !saving && !loading,
    canGoPrev: !saving && !loading,
    isLastStep,
  }), [handleNext, handlePrev, isStepReady, saving, loading, isLastStep]);

  const contextValue = useMemo(() => ({
    shopId,
    client,
    config,
    saveConfig,
    updateViewIntegration,
    experienceType,
    isExpanded,
    onExpandToggle,
    onUpdate: handleStepUpdate,
    onReady: handleStepReady,
    onRegisterSave: handleRegisterSave,
  }), [
    shopId, client, config, saveConfig, updateViewIntegration,
    experienceType,
    isExpanded, onExpandToggle, handleStepUpdate, handleStepReady,
    handleRegisterSave,
  ]);

  if (!client) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          padding: '2rem',
        }}
        data-testid="shell-client-missing"
      >
        <p>
          {localizeMessage('wizard.searchSetup.configuration.sessionUnavailable')}
          {' '}
          <a href="/embedded/app">
            {localizeMessage('wizard.searchSetup.configuration.retry')}
          </a>
        </p>
      </div>
    );
  }

  if (!currentStep) {
    return (
      <Box className={styles.root}>
        <Typography>
          {localizeMessage('wizard.searchSetup.configuration.noSteps', {
            experience: experienceType,
          })}
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={200}
      >
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <IntegrationWizardContext.Provider value={contextValue}>
      <Box className={styles.root}>
        <Box className={styles.content}>
          <AnimatedStepSwitcher
            stepKey={currentStepIndex}
          >
            <div style={{ height: '100%', width: '100%' }}>
              <StepRouter
                stepId={currentStep.id}
                title={localizeMessage(currentStep.titleKey)}
                description={localizeMessage(currentStep.descriptionKey)}
                data={data || {}}
              />
            </div>
          </AnimatedStepSwitcher>
        </Box>

        {portalContainer && ReactDOM.createPortal(
          <Box className={styles.dotsInner}>
            {steps.map((step, index) => {
              const isCompleted = index < currentStepIndex;
              let dotClass = '';
              if (index === currentStepIndex) {
                dotClass = styles.active;
              } else if (isCompleted) {
                dotClass = styles.completed;
              }
              return (
                <DotIcon
                  key={step.id}
                  className={`${styles.dot} ${dotClass}`}
                  fontSize="inherit"
                  onClick={() => {
                    if (isCompleted) {
                      setCurrentStepIndex(index);
                    }
                  }}
                  style={{ cursor: isCompleted ? 'pointer' : 'default' }}
                />
              );
            })}
          </Box>,
          portalContainer,
        )}
      </Box>
    </IntegrationWizardContext.Provider>
  );
});

EmbeddedWizard.displayName = 'EmbeddedWizard';

EmbeddedWizard.propTypes = {
  experienceType: PropTypes.string.isRequired,
  shopId: PropTypes.string.isRequired,
  client: PropTypes.shape({
    loadSearchExperienceConfig: PropTypes.func,
    saveSearchExperienceConfig: PropTypes.func,
  }),
  onComplete: PropTypes.func.isRequired,
  onReady: PropTypes.func,
  onNavigationChange: PropTypes.func,
  isExpanded: PropTypes.bool,
  onExpandToggle: PropTypes.func,
};

EmbeddedWizard.defaultProps = {
  client: null,
  onReady: null,
  onNavigationChange: null,
  isExpanded: false,
  onExpandToggle: null,
};

export default EmbeddedWizard;
